<script setup lang="ts">
import { useTranslation } from "i18next-vue";

withDefaults(
  defineProps<{
    modelValue: boolean;
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    loading?: boolean;
    danger?: boolean;
    hideCancel?: boolean;
  }>(),
  {
    confirmLabel: undefined,
    cancelLabel: undefined,
    loading: false,
    danger: false,
    hideCancel: false,
  },
);

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "confirm"): void;
  (e: "cancel"): void;
}>();

const { t } = useTranslation();

const close = () => {
  emit("update:modelValue", false);
  emit("cancel");
};

const confirm = () => {
  emit("confirm");
};
</script>

<template>
  <Teleport to="body">
    <div v-if="modelValue" class="mobile-confirm-overlay" @click.self="close">
      <div class="mobile-confirm-content">
        <h4>{{ title }}</h4>
        <p class="confirm-message">{{ message }}</p>
        <div class="confirm-actions">
          <button v-if="!hideCancel" class="confirm-btn cancel" @click="close">
            {{ cancelLabel ?? t("cancel") }}
          </button>
          <button
            class="confirm-btn"
            :class="danger ? 'danger' : 'primary'"
            :disabled="loading"
            @click="confirm"
          >
            <span
              v-if="loading"
              class="spinner-border spinner-border-sm me-2"
            />
            {{ confirmLabel ?? t("confirm") }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
.mobile-confirm-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0;
}

.mobile-confirm-content {
  width: 100%;
  background-color: var(--bs-white);
  border-radius: 20px 20px 0 0;
  padding: 24px;
  padding-bottom: max(24px, env(safe-area-inset-bottom));

  h4 {
    margin: 0 0 12px;
    font-size: 18px;
    font-weight: 600;
    color: var(--bs-body-color);
  }
}

.confirm-message {
  color: var(--bs-secondary-color);
  font-size: 15px;
  margin: 0;
  line-height: 1.5;
}

.confirm-actions {
  display: flex;
  gap: 12px;
  margin-top: 24px;
}

.confirm-btn {
  flex: 1;
  padding: 14px 20px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &.cancel {
    background-color: var(--bs-secondary-bg);
    color: var(--bs-body-color);
  }

  &.primary {
    background-color: var(--bs-primary);
    color: white;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &.danger {
    background-color: #ef4444;
    color: white;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}
</style>
